//----------------------------------------------------------------------------------------------------------------------
// Entity Manager
//----------------------------------------------------------------------------------------------------------------------

import type { TransformNode } from '@babylonjs/core';

import { GameEntity, type RequestResult } from '../classes/entity.ts';
import { GameEventBus } from '../classes/eventBus.ts';
import type { GameEngine } from '../classes/gameEngine.ts';

// Interfaces
import type { Action } from '../interfaces/action.ts';
import type { GameEntityDefinition } from '../interfaces/entity.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LoggerInterface } from '../interfaces/logger.ts';
import type { BindingManager } from '../managers/binding.ts';

// Utils
import { LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Options for creating an entity instance.
 */
export interface CreateEntityOptions<State extends object = object>
{
    /** Initial state to merge with defaultState. */
    initialState ?: Partial<State>;

    /** Override name from entity definition. */
    name ?: string;

    /** Additional tags to add (merged with definition tags). */
    tags ?: string[];

    /** Scene node to attach the entity to. */
    node ?: TransformNode;
}

//----------------------------------------------------------------------------------------------------------------------

export class GameEntityManager implements Disposable
{
    /** The event bus for the entity manager. */
    private eventBus : GameEventBus;

    /** A map of entities managed by the entity manager. */
    private entities : Map<string, GameEntity> = new Map<string, GameEntity>();

    /** A map of entity definitions registered with the entity manager. */
    private entityDefinitions : Map<string, GameEntityDefinition> = new Map<string, GameEntityDefinition>();

    /** Late-bound GameEngine reference */
    private _gameEngine : GameEngine | null = null;

    /** Reference to the binding manager for registering actions */
    private bindingManager : BindingManager;

    /** Logger instance */
    private _log : LoggerInterface;

    //------------------------------------------------------------------------------------------------------------------
    // Indices for fast lookups
    //------------------------------------------------------------------------------------------------------------------

    /** Index: name -> entity (names are not unique, first match returned by getByName) */
    private _entitiesByName : Map<string, Set<GameEntity>> = new Map<string, Set<GameEntity>>();

    /** Index: type -> entities */
    private _entitiesByType : Map<string, Set<GameEntity>> = new Map<string, Set<GameEntity>>();

    /** Index: tag -> entities */
    private _entitiesByTag : Map<string, Set<GameEntity>> = new Map<string, Set<GameEntity>>();

    /** Index: node -> entity (one entity per node) */
    private _entitiesByNode : Map<TransformNode, GameEntity> = new Map<TransformNode, GameEntity>();

    /** Object pool storage: type -> pooled entities */
    private _pools : Map<string, GameEntity[]> = new Map<string, GameEntity[]>();

    /**
     * Creates an instance of EntityManager.
     * @param eventBus
     * @param logger
     * @param bindingManager
     */
    constructor(eventBus : GameEventBus, logger : LoggingUtility | undefined, bindingManager : BindingManager)
    {
        this.eventBus = eventBus;
        this.bindingManager = bindingManager;
        this._log = logger?.getLogger('EntityManager') || new SAGELogger('EntityManager');
        this._log.info('EntityManager initialized');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Internal API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Late-bind the GameEngine after construction.
     * Called by createGameEngine() after the GameEngine instance is created.
     */
    $setGameEngine(gameEngine : GameEngine) : void
    {
        this._gameEngine = gameEngine;
    }

    private get _engine() : GameEngine
    {
        if(!this._gameEngine)
        {
            throw new Error('EntityManager: gameEngine not set. Call $setGameEngine() first.');
        }
        return this._gameEngine;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods - Index Management
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Gets or creates a Set in a Map, returning the Set.
     */
    private _getOrCreateSet<K>(map : Map<K, Set<GameEntity>>, key : K) : Set<GameEntity>
    {
        let set = map.get(key);
        if(!set)
        {
            set = new Set<GameEntity>();
            map.set(key, set);
        }
        return set;
    }

    /**
     * Adds an entity to all relevant indices.
     */
    private _indexEntity(entity : GameEntity) : void
    {
        // Index by type
        this._getOrCreateSet(this._entitiesByType, entity.type).add(entity);

        // Index by name (if present)
        if(entity.name)
        {
            this._getOrCreateSet(this._entitiesByName, entity.name).add(entity);
        }

        // Index by tags
        for(const tag of entity.tags)
        {
            this._getOrCreateSet(this._entitiesByTag, tag).add(entity);
        }

        // Index by node (if attached)
        if(entity.node)
        {
            this._entitiesByNode.set(entity.node, entity);
        }
    }

    /**
     * Removes an entity from all indices.
     */
    private _unindexEntity(entity : GameEntity) : void
    {
        // Remove from type index
        const typeSet = this._entitiesByType.get(entity.type);
        if(typeSet)
        {
            typeSet.delete(entity);
            if(typeSet.size === 0)
            {
                this._entitiesByType.delete(entity.type);
            }
        }

        // Remove from name index
        if(entity.name)
        {
            const nameSet = this._entitiesByName.get(entity.name);
            if(nameSet)
            {
                nameSet.delete(entity);
                if(nameSet.size === 0)
                {
                    this._entitiesByName.delete(entity.name);
                }
            }
        }

        // Remove from all tag indices
        for(const tag of entity.tags)
        {
            const tagSet = this._entitiesByTag.get(tag);
            if(tagSet)
            {
                tagSet.delete(entity);
                if(tagSet.size === 0)
                {
                    this._entitiesByTag.delete(tag);
                }
            }
        }

        // Remove from node index
        if(entity.node)
        {
            this._entitiesByNode.delete(entity.node);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods - Inheritance
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Resolves an entity definition that extends a base definition by merging according to inheritance rules.
     * The base definition must already be registered.
     */
    private _resolveInheritance(childDef : GameEntityDefinition) : GameEntityDefinition
    {
        const baseType = childDef.extends;
        if(!baseType)
        {
            return childDef;
        }

        const baseDef = this.entityDefinitions.get(baseType);
        if(!baseDef)
        {
            const errorMsg = `Cannot extend "${ baseType }": base entity definition is not registered.`;
            this._log.error(errorMsg);
            throw new Error(errorMsg);
        }

        // Merge tags: concatenate and deduplicate
        let mergedTags : string[] | undefined;
        if(baseDef.tags || childDef.tags)
        {
            mergedTags = [ ...new Set([ ...(baseDef.tags ?? []), ...(childDef.tags ?? []) ]) ];
        }

        // Build resolved definition — child overrides base for scalar/replace fields
        const resolved : GameEntityDefinition = {
            // Scalar fields: child overrides if provided, else inherit
            type: childDef.type,
            name: childDef.name ?? baseDef.name,
            mesh: childDef.mesh ?? baseDef.mesh,

            // Shallow-merged state
            defaultState: { ...baseDef.defaultState, ...childDef.defaultState },

            // Replace-entirely fields: child overrides if provided, else inherit
            behaviors: childDef.behaviors ?? baseDef.behaviors,
            actions: childDef.actions ?? baseDef.actions,

            // Concatenated tags
            tags: mergedTags,

            // Pooling: child overrides if provided, else inherit
            poolable: childDef.poolable ?? baseDef.poolable,
            poolSize: childDef.poolSize ?? baseDef.poolSize,

            // Children: child overrides if provided, else inherit
            children: childDef.children ?? baseDef.children,

            // Lifecycle hooks: child overrides if provided, else inherit
            onBeforeCreate: childDef.onBeforeCreate ?? baseDef.onBeforeCreate,
            onCreate: childDef.onCreate ?? baseDef.onCreate,
            onBeforeDestroy: childDef.onBeforeDestroy ?? baseDef.onBeforeDestroy,
            onDestroy: childDef.onDestroy ?? baseDef.onDestroy,
        };

        return resolved;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods - Actions
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Checks if two actions are compatible (same type and non-conflicting options).
     * @param existingAction - The action already registered
     * @param newAction - The action being registered
     */
    private _areActionsCompatible(existingAction : Action, newAction : Action) : boolean
    {
        // If types don't match, they're incompatible
        if(existingAction.type !== newAction.type)
        {
            return false;
        }

        // For analog actions, check min and max values
        if(existingAction.type === 'analog' && newAction.type === 'analog')
        {
            // If minValue or maxValue is specified and differs, they're incompatible
            if((newAction.minValue !== undefined && existingAction.minValue !== newAction.minValue)
                || (newAction.maxValue !== undefined && existingAction.maxValue !== newAction.maxValue))
            {
                return false;
            }
        }

        // Digital actions are always compatible if they have the same name
        return true;
    }

    /**
     * Registers actions defined in the entity definition with the binding manager.
     * Skips actions already registered with compatible options; warns on conflicts.
     * @param entityDef
     */
    private _registerEntityActions(entityDef : GameEntityDefinition) : void
    {
        if(!entityDef.actions)
        {
            return;
        }

        for(const action of entityDef.actions)
        {
            try
            {
                // Check if the action is already registered
                const existingAction = this.bindingManager.getAction(action.name);

                if(!existingAction)
                {
                    // Action doesn't exist yet, register it
                    this._log.debug(`Registering action "${ action.name }" from entity type "${ entityDef.type }"`);
                    this.bindingManager.registerAction(action);
                }
                else if(!this._areActionsCompatible(existingAction, action))
                {
                    this._log.warn(
                        `Action "${ action.name }" already registered with different options. `
                        + `Entity "${ entityDef.type }" requires: ${ JSON.stringify(action) }, `
                        + `but found: ${ JSON.stringify(existingAction) }`
                    );
                }
                else
                {
                    this._log.trace(
                        `Action "${ action.name }" already registered with compatible options, skipping registration`
                    );
                }
            }
            catch (err)
            {
                this._log.debug(`Failed to register action "${ action.name }": `
                    + `${ err instanceof Error ? err.message : String(err) }`);
            }
        }
    }

    $frameUpdate(dt : number) : void
    {
        this._log.trace(`Updating ${ this.entities.size } entities with dt=${ dt }`);
        for(const entity of this.entities.values())
        {
            // Only update root entities; children are cascaded via parent.$update()
            if(!entity.parent)
            {
                entity.$update(dt);
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Registers a new entity definition.
     * Also registers any actions declared in the definition with the binding manager.
     * @param entityDef
     */
    registerEntityDefinition(entityDef : GameEntityDefinition) : void
    {
        this._log.debug(`Registering entity definition: ${ entityDef.type }`);

        // Resolve inheritance if extends is specified
        const resolved = entityDef.extends
            ? this._resolveInheritance(entityDef)
            : entityDef;

        // Register any actions this entity requires
        this._registerEntityActions(resolved);

        // Store the resolved entity definition
        this.entityDefinitions.set(resolved.type, resolved);

        // Auto-prewarm pool if poolSize is specified
        if(resolved.poolSize && resolved.poolSize > 0)
        {
            // Prewarm is async but registration is sync — fire and forget is fine here
            // because prewarm just creates entities and pools them
            void this.prewarm(resolved.type, resolved.poolSize);
        }
    }

    /**
     * Retrieve an entity definition by type.
     * @param type
     */
    getDefinition(type : string) : GameEntityDefinition | undefined
    {
        return this.entityDefinitions.get(type);
    }

    /**
     * Creates a new entity of the given type.
     * Runs onBeforeCreate/onCreate lifecycle hooks, merges state, applies tags, and indexes the entity.
     * @param type - Must match a previously registered entity definition
     * @param options
     */
    async createEntity<
        State extends object = object,
    >
    (
        type : string,
        options : CreateEntityOptions<State> = {}
    ) : Promise<GameEntity<State>>
    {
        this._log.debug(`Creating entity of type: ${ type }`);
        const entityDef = this.entityDefinitions.get(type);
        if(!entityDef)
        {
            const errorMsg = `Entity type ${ type } is not registered.`;
            this._log.error(errorMsg);
            throw new Error(errorMsg);
        }

        // Check pool for recyclable entity
        if(entityDef.poolable)
        {
            const pool = this._pools.get(type);
            if(pool && pool.length > 0)
            {
                this._log.trace(`Recycling entity from pool for type: ${ type }`);
                const entity = pool.pop() as GameEntity<State>;

                // Reset with definition defaults
                entity.$reset(
                    entityDef.defaultState,
                    entityDef.tags ?? []
                );

                // Re-wire the entity manager back-reference
                entity.$setEntityManager(this);

                // Apply any initialState overrides
                if(options.initialState)
                {
                    entity.state = { ...entity.state, ...options.initialState } as State;
                }

                // Apply additional tags from options
                if(options.tags)
                {
                    for(const tag of options.tags)
                    {
                        entity.$addTag(tag);
                    }
                }

                // Attach to node if provided
                if(options.node)
                {
                    entity.$attachToNode(options.node, this._engine);
                }

                // Re-index
                this.entities.set(entity.id, entity);
                this._indexEntity(entity);

                this._log.debug(`Recycled entity ${ entity.id } from pool`);
                return entity;
            }
        }

        this._log.trace(`Using entity definition with ${ entityDef.behaviors?.length || 0 } behaviors`);
        let mergedState = { ...entityDef.defaultState, ...options.initialState } as State;

        // Call onBeforeCreate hook if it exists
        if(entityDef.onBeforeCreate)
        {
            this._log.trace(`Calling onBeforeCreate hook for entity type: ${ type }`);
            const result = await entityDef.onBeforeCreate(mergedState);
            // If the hook returns a new state, use it
            if(result !== undefined)
            {
                mergedState = result as State;
            }
        }

        // Determine entity name (options override definition)
        const entityName = options.name ?? entityDef.name;

        // Create the entity instance with possibly modified state
        const entity = new GameEntity<State>(
            entityDef.type,
            this.eventBus,
            mergedState,
            entityDef.behaviors,
            entityName
        );

        // Wire up the entity manager back-reference for cross-entity messaging
        entity.$setEntityManager(this);

        // Attach to node if provided
        if(options.node)
        {
            entity.$attachToNode(options.node, this._engine);
        }

        // Apply tags from definition
        if(entityDef.tags)
        {
            for(const tag of entityDef.tags)
            {
                entity.$addTag(tag);
            }
        }

        // Apply additional tags from options
        if(options.tags)
        {
            for(const tag of options.tags)
            {
                entity.$addTag(tag);
            }
        }

        // Add entity to the manager and index it
        this.entities.set(entity.id, entity);
        this._indexEntity(entity);
        this._log.debug(`Entity created with ID: ${ entity.id }${ entityName ? ` (name: ${ entityName })` : '' }`);

        // Call onCreate hook if it exists
        if(entityDef.onCreate)
        {
            this._log.trace(`Calling onCreate hook for entity type: ${ type }`);
            const result = await entityDef.onCreate(entity.state);
            // If the hook returns a new state, update the entity's state
            if(result !== undefined)
            {
                entity.state = result as State;
            }
        }

        // Create child entities if defined
        if(entityDef.children)
        {
            for(const childDef of entityDef.children)
            {
                const childInitialState : Record<string, unknown> = { ...childDef.initialState };

                // Store position/rotation in initial state for later application
                if(childDef.position)
                {
                    childInitialState._position = childDef.position;
                }
                if(childDef.rotation)
                {
                    childInitialState._rotation = childDef.rotation;
                }

                // eslint-disable-next-line no-await-in-loop
                const childEntity = await this.createEntity(childDef.type, {
                    name: childDef.name,
                    initialState: childInitialState,
                });

                entity.$addChild(childEntity);
            }
        }

        return entity;
    }

    /**
     * Destroys the entity with the given ID.
     * Runs onBeforeDestroy/onDestroy lifecycle hooks, unindexes, and removes the entity.
     * @param entityID
     */
    async destroyEntity(entityID : string) : Promise<void>
    {
        this._log.debug(`Destroying entity: ${ entityID }`);
        const entity = this.entities.get(entityID);
        if(entity)
        {
            // Get entity definition
            const entityDef = this.entityDefinitions.get(entity.type);

            // Check if this entity should be pooled instead of destroyed
            if(entityDef?.poolable)
            {
                this._log.trace(`Pooling entity ${ entityID } instead of destroying`);

                // Pool children first (iterate copy to avoid mutation during iteration)
                for(const child of [ ...entity.children ])
                {
                    // eslint-disable-next-line no-await-in-loop
                    await this.destroyEntity(child.id);
                }

                // If the entity has a parent, remove it from the parent's children
                if(entity.parent)
                {
                    entity.parent.$removeChild(entity);
                }

                // Remove from all indices
                this._unindexEntity(entity);

                // Reset the entity
                entity.$reset(entityDef.defaultState, entityDef.tags ?? []);

                // Push to pool
                let pool = this._pools.get(entity.type);
                if(!pool)
                {
                    pool = [];
                    this._pools.set(entity.type, pool);
                }
                pool.push(entity);

                // Remove from active entities
                this.entities.delete(entityID);

                this._log.debug(`Entity ${ entityID } pooled`);
                return;
            }

            // Non-poolable: destroy normally

            // Destroy children first (iterate copy to avoid mutation during iteration)
            for(const child of [ ...entity.children ])
            {
                // eslint-disable-next-line no-await-in-loop
                await this.destroyEntity(child.id);
            }

            // If the entity has a parent, remove it from the parent's children
            if(entity.parent)
            {
                entity.parent.$removeChild(entity);
            }

            // Call onBeforeDestroy hook if it exists
            if(entityDef?.onBeforeDestroy)
            {
                this._log.trace(`Calling onBeforeDestroy hook for entity: ${ entityID }`);
                const result = await entityDef.onBeforeDestroy(entity.state);
                // If the hook returns a new state, update the entity's state
                if(result !== undefined)
                {
                    entity.state = result;
                }
            }

            // Remove from indices BEFORE destroying
            this._unindexEntity(entity);

            // Destroy entity (call $destroy internally)
            await entity.$destroy();

            // Call onDestroy hook if it exists
            if(entityDef?.onDestroy)
            {
                this._log.trace(`Calling onDestroy hook for entity: ${ entityID }`);
                await entityDef.onDestroy(entity.state);
            }

            // Remove entity from manager
            this.entities.delete(entityID);

            this._log.debug(`Entity ${ entityID } destroyed`);
        }
        else
        {
            this._log.warn(`Attempted to destroy non-existent entity: ${ entityID }`);
        }
    }

    /**
     * Gets the entity with the given ID.
     * @param entityID
     */
    getEntity(entityID : string) : GameEntity | null
    {
        this._log.trace(`Getting entity: ${ entityID }`);
        return this.entities.get(entityID) ?? null;
    }

    /**
     * Adds an existing entity to the entity manager and indexes it.
     * @param entity
     */
    addEntity(entity : GameEntity) : void
    {
        this._log.debug(`Adding existing entity: ${ entity.id } (type: ${ entity.type })`);
        entity.$setEntityManager(this);
        this.entities.set(entity.id, entity);
        this._indexEntity(entity);
    }

    /**
     * Removes the entity with the given ID, without destroying it.
     * @param entityID
     */
    removeEntity(entityID : string) : void
    {
        this._log.debug(`Removing entity: ${ entityID }`);
        const entity = this.entities.get(entityID);
        if(entity)
        {
            this._unindexEntity(entity);
            this.entities.delete(entityID);
            this._log.debug(`Entity ${ entityID } removed`);
        }
        else
        {
            this._log.warn(`Attempted to remove non-existent entity: ${ entityID }`);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Parent-Child Hierarchy
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Adds a child entity to a parent entity.
     * @param parentId - ID of the parent entity
     * @param childId - ID of the child entity
     * @returns False if either entity is not found
     */
    addChild(parentId : string, childId : string) : boolean
    {
        const parent = this.entities.get(parentId);
        const child = this.entities.get(childId);

        if(!parent)
        {
            this._log.warn(`addChild: parent entity "${ parentId }" not found`);
            return false;
        }

        if(!child)
        {
            this._log.warn(`addChild: child entity "${ childId }" not found`);
            return false;
        }

        parent.$addChild(child);
        this._log.trace(`Added child ${ childId } to parent ${ parentId }`);
        return true;
    }

    /**
     * Removes a child entity from a parent entity.
     * @param parentId - ID of the parent entity
     * @param childId - ID of the child entity
     * @returns False if either entity is not found
     */
    removeChild(parentId : string, childId : string) : boolean
    {
        const parent = this.entities.get(parentId);
        const child = this.entities.get(childId);

        if(!parent)
        {
            this._log.warn(`removeChild: parent entity "${ parentId }" not found`);
            return false;
        }

        if(!child)
        {
            this._log.warn(`removeChild: child entity "${ childId }" not found`);
            return false;
        }

        parent.$removeChild(child);
        this._log.trace(`Removed child ${ childId } from parent ${ parentId }`);
        return true;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Query Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Gets the first entity with the given name.
     * Names are not unique; use getEntitiesByName() to retrieve all matches.
     * @param name
     */
    getByName(name : string) : GameEntity | undefined
    {
        const nameSet = this._entitiesByName.get(name);
        if(nameSet && nameSet.size > 0)
        {
            return nameSet.values().next().value;
        }
        return undefined;
    }

    /**
     * Gets all entities with the given name.
     * @param name
     */
    getEntitiesByName(name : string) : GameEntity[]
    {
        const nameSet = this._entitiesByName.get(name);
        return nameSet ? Array.from(nameSet) : [];
    }

    /**
     * Gets all entities of the given type.
     * @param type
     */
    getByType(type : string) : GameEntity[]
    {
        const typeSet = this._entitiesByType.get(type);
        return typeSet ? Array.from(typeSet) : [];
    }

    /**
     * Gets all entities with the given tag.
     * @param tag
     */
    getByTag(tag : string) : GameEntity[]
    {
        const tagSet = this._entitiesByTag.get(tag);
        return tagSet ? Array.from(tagSet) : [];
    }

    /**
     * Gets all entities matching multiple tags.
     * @param tags
     * @param mode - 'all' requires every tag (intersection), 'any' requires at least one (union)
     */
    getByTags(tags : string[], mode : 'all' | 'any' = 'all') : GameEntity[]
    {
        if(tags.length === 0)
        {
            return [];
        }

        if(mode === 'any')
        {
            // Union - entities with ANY of the tags
            const result = new Set<GameEntity>();
            for(const tag of tags)
            {
                const tagSet = this._entitiesByTag.get(tag);
                if(tagSet)
                {
                    for(const entity of tagSet)
                    {
                        result.add(entity);
                    }
                }
            }
            return Array.from(result);
        }
        else
        {
            // Intersection - entities with ALL tags
            // Start with smallest set for efficiency
            const sets = tags
                .map((tag) => this._entitiesByTag.get(tag))
                .filter((set) : set is Set<GameEntity> => set !== undefined);

            // If any tag has no entities, result is empty
            if(sets.length !== tags.length)
            {
                return [];
            }

            // Sort by size (smallest first) for efficient intersection
            sets.sort((setA, setB) => setA.size - setB.size);
            const [ smallest, ...rest ] = sets;

            return Array.from(smallest).filter((entity) => rest.every((set) => set.has(entity)));
        }
    }

    /**
     * Returns an iterator over all entities.
     * More memory-efficient than returning an array for large entity counts.
     */
    getAllEntities() : IterableIterator<GameEntity>
    {
        return this.entities.values();
    }

    /**
     * Gets the total number of entities.
     */
    get entityCount() : number
    {
        return this.entities.size;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Messaging
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Sends a fire-and-forget event directly to a specific entity's behaviors, bypassing the event bus.
     * Resolves the target by entity ID first (O(1)), then falls back to name lookup (O(1)).
     *
     * @param target - Entity ID or name
     * @param type - Event type string
     * @param payload - Optional event payload
     */
    async send(target : string, type : string, payload ?: unknown, senderID ?: string) : Promise<void>
    {
        const entity = this.entities.get(target) ?? this.getByName(target);
        if(!entity)
        {
            this._log.warn(`send: entity "${ target }" not found`);
            return;
        }

        await entity.$send(type, payload, senderID);
    }

    /**
     * Sends a request to a specific entity's behaviors and returns the first response.
     * Resolves the target by entity ID first (O(1)), then falls back to name lookup (O(1)).
     *
     * @param target - Entity ID or name
     * @param type - Request type string
     * @param payload - Optional request payload
     * @param senderID - Optional sender entity ID (stamped on the event)
     */
    async request<T>(
        target : string,
        type : string,
        payload ?: unknown,
        senderID ?: string
    ) : Promise<RequestResult<T>>
    {
        const entity = this.entities.get(target) ?? this.getByName(target);
        if(!entity)
        {
            return { success: false, error: `Entity "${ target }" not found` };
        }

        return entity.$request<T>(type, payload, senderID);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Tag Manipulation
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Adds a tag to an entity and updates the tag index.
     * @param entity - Entity instance or entity ID
     * @param tag
     * @returns False if already present or entity not found
     */
    addTag(entity : GameEntity | string, tag : string) : boolean
    {
        const targetEntity = typeof entity === 'string' ? this.entities.get(entity) : entity;
        if(!targetEntity)
        {
            this._log.warn(`addTag: entity not found`);
            return false;
        }

        if(targetEntity.hasTag(tag))
        {
            return false; // Already has tag
        }

        // Add to entity and update index
        targetEntity.$addTag(tag);
        this._getOrCreateSet(this._entitiesByTag, tag).add(targetEntity);

        this._log.trace(`Added tag "${ tag }" to entity ${ targetEntity.id }`);
        return true;
    }

    /**
     * Removes a tag from an entity and updates the tag index.
     * @param entity - Entity instance or entity ID
     * @param tag
     * @returns False if not present or entity not found
     */
    removeTag(entity : GameEntity | string, tag : string) : boolean
    {
        const targetEntity = typeof entity === 'string' ? this.entities.get(entity) : entity;
        if(!targetEntity)
        {
            this._log.warn(`removeTag: entity not found`);
            return false;
        }

        if(!targetEntity.$removeTag(tag))
        {
            return false; // Didn't have tag
        }

        // Update index
        const tagSet = this._entitiesByTag.get(tag);
        if(tagSet)
        {
            tagSet.delete(targetEntity);
            if(tagSet.size === 0)
            {
                this._entitiesByTag.delete(tag);
            }
        }

        this._log.trace(`Removed tag "${ tag }" from entity ${ targetEntity.id }`);
        return true;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Node Attachment
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Gets an entity by its attached scene node.
     * @param node
     */
    getByNode(node : TransformNode) : GameEntity | undefined
    {
        return this._entitiesByNode.get(node);
    }

    /**
     * Attaches an entity to a scene node.
     * The entity will reference this node and can be looked up via getByNode().
     *
     * @param entity - Entity instance or entity ID
     * @param node
     * @returns False if entity not found or node already has an entity
     */
    attachToNode(entity : GameEntity | string, node : TransformNode) : boolean
    {
        const targetEntity = typeof entity === 'string' ? this.entities.get(entity) : entity;
        if(!targetEntity)
        {
            this._log.warn(`attachToNode: entity not found`);
            return false;
        }

        // Check if node already has an entity
        if(this._entitiesByNode.has(node))
        {
            this._log.warn(`attachToNode: node already has an attached entity`);
            return false;
        }

        // Detach from current node if any
        if(targetEntity.node)
        {
            this._entitiesByNode.delete(targetEntity.node);
        }

        // Attach to new node
        targetEntity.$attachToNode(node, this._engine);
        this._entitiesByNode.set(node, targetEntity);

        this._log.trace(`Attached entity ${ targetEntity.id } to node "${ node.name }"`);
        return true;
    }

    /**
     * Detaches an entity from its scene node.
     *
     * @param entity - Entity instance or entity ID
     * @returns False if entity not found or had no node
     */
    detachFromNode(entity : GameEntity | string) : boolean
    {
        const targetEntity = typeof entity === 'string' ? this.entities.get(entity) : entity;
        if(!targetEntity)
        {
            this._log.warn(`detachFromNode: entity not found`);
            return false;
        }

        if(!targetEntity.node)
        {
            return false; // Nothing to detach
        }

        // Remove from index
        this._entitiesByNode.delete(targetEntity.node);

        // Detach entity
        const nodeName = targetEntity.node.name;
        targetEntity.$detachFromNode();

        this._log.trace(`Detached entity ${ targetEntity.id } from node "${ nodeName }"`);
        return true;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Object Pooling
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Pre-creates entities and pools them for fast recycling.
     * @param type - Entity type to prewarm
     * @param count - Number of entities to create and pool
     */
    async prewarm(type : string, count : number) : Promise<void>
    {
        this._log.debug(`Prewarming pool for "${ type }" with ${ count } entities`);

        for(let i = 0; i < count; i++)
        {
            // eslint-disable-next-line no-await-in-loop
            const entity = await this.createEntity(type);
            // eslint-disable-next-line no-await-in-loop
            await this.destroyEntity(entity.id);
        }
    }

    /**
     * Drains the pool for a given type, actually destroying all pooled entities.
     * @param type - Entity type whose pool to drain
     */
    async drainPool(type : string) : Promise<void>
    {
        const pool = this._pools.get(type);
        if(!pool)
        {
            return;
        }

        this._log.debug(`Draining pool for "${ type }" (${ pool.length } entities)`);

        for(const entity of pool)
        {
            // eslint-disable-next-line no-await-in-loop
            await entity.$destroy();
        }

        pool.length = 0;
        this._pools.delete(type);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Tears down all entities and releases resources.
     * Destroys entities individually (running lifecycle hooks), then clears definitions and indices.
     */
    async $teardown() : Promise<void>
    {
        this._log.info(`Tearing down EntityManager with ${ this.entities.size } entities`);

        // Create a copy of the entity IDs to avoid modification during iteration
        const entityIds = [ ...this.entities.keys() ];

        // Destroy all entities
        for(const entityId of entityIds)
        {
            try
            {
                // eslint-disable-next-line no-await-in-loop
                await this.destroyEntity(entityId);
            }
            catch (err)
            {
                this._log.error(`Error destroying entity ${ entityId }: ${ err }`);
            }
        }

        // Drain all pools
        for(const type of [ ...this._pools.keys() ])
        {
            try
            {
                // eslint-disable-next-line no-await-in-loop
                await this.drainPool(type);
            }
            catch (err)
            {
                this._log.error(`Error draining pool for "${ type }": ${ err }`);
            }
        }

        // Clear entity definitions
        this.entityDefinitions.clear();

        // Clear indices
        this._entitiesByName.clear();
        this._entitiesByType.clear();
        this._entitiesByTag.clear();
        this._entitiesByNode.clear();

        this._log.info('EntityManager torn down successfully');
    }
}

//----------------------------------------------------------------------------------------------------------------------

